import {
  GAME_DATA,
  TERRITORY_RULES,
  type MapView,
  type Supply,
  type OwnedSquishy,
  type PlayerBattle,
  type PublicTile,
  type PublicUser,
  type Species,
  type TerritoryStatus,
} from '@heartpatch/shared';
import { countdownAt, GameClock } from '../inventory/game-clock.js';
import { COMMAND_RETRY_MS, sendCommand } from '../inventory/send-command.js';
import type { TileActions } from '../map/map-screen.js';
import { ApiRequestError } from '../net/api.js';
import { newIdempotencyKey } from '../net/idempotency-key.js';
import { buttonClass } from '../ui/button/button.js';
import { el, messageOf } from '../ui/dom.js';
import { territoryApi, type TerritoryApi } from './territory-api.js';
import { crackedBy, fenceCard } from '../fences/fence-model.js';
import {
  isRestoredVillageTile,
  showsGuardLevelNote,
  territoryAction,
  watchInTheDark,
  type TerritoryAction,
} from './territory-action.js';
import { watchFeelingsLine } from '../map/tile-info.js';
import './territory.css';

// Territory in the tile panel (#15, design doc §11): "Claim" wild land next
// to yours, "Challenge" a neighbour's (style guide §9), and pick who stands
// watch on your own land. The server checks every raid rule and runs the
// battle (CLAUDE.md rule 1); this only shows what it says and sends taps.

export interface TerritoryScreenOptions {
  api?: TerritoryApi;
  /** A tile battle started (or one going came back): the battle screen takes over. */
  openBattle: (battle: PlayerBattle) => void;
  /** Device wall clock in ms (tests pass a fake). */
  now?: () => number;
  /** My supply line on a Wild Isle (#621), for what counts as next to my land; none on a Cozy Patch. */
  supply?: (view: MapView) => Supply | undefined;
  /** The week of a Long Night (#727): "He'll come looking on Saturday 🔥", added to the dark-land line. */
  darkSoon?: () => string | null;
}

/** Read-only state for the dev hook (Playwright asserts on it, not on pixels). */
export interface TerritoryDebug {
  readonly mapId: string;
  readonly attemptsLeft: number;
  /** Squishies on watch across my land. */
  readonly onWatch: number;
  /** The tile panel's land action right now, if any. */
  readonly tileAction: TerritoryAction['kind'] | null;
  readonly picking: boolean;
}

export interface TerritoryScreen {
  /** The map on screen (null: none); fetches tries left and who's on watch. */
  setMap: (mapId: string | null) => Promise<void>;
  setUser: (user: PublicUser | null) => void;
  readonly tileActions: TileActions;
  readonly debug: TerritoryDebug | null;
}

// Player-facing text (style guide §2, §6, §9: Claim, Challenge, never "attack").
export const TERRITORY_TEXT = {
  claim: 'Claim',
  challenge: 'Challenge',
  claimNote: 'Wild land! Win a showdown with its guardians to make it yours.',
  challengeNote: (owner: string) => `${owner}'s land. Win a showdown to make it yours!`,
  someone: 'Someone',
  // No numbers: the bump over the Partner's level is tunable (#529).
  guardsAsStrong:
    "Their guards are as strong as their Keeper's Partner, or stronger. Bring your best!",
  onWatchThere: (n: number) =>
    n === 1 ? '1 squishy stands watch here.' : `${String(n)} squishies stand watch here.`,
  // Relative times, never clock times: the patch's midnight isn't every player's (#201).
  triesLeft: (n: number, wait: string) =>
    `${n === 1 ? '1 try' : `${String(n)} tries`} left · new tries in ${wait}`,
  resting: (wait: string) => `This land needs a rest. Try again in ${wait}.`,
  noTries: (wait: string) => `New tries in ${wait} 🌙`,
  pvpOff: 'Challenges are off on this patch.',
  // The daily loss cap (#526), as the server says it, counting down to the patch's midnight.
  keeperResting: (owner: string, wait: string) =>
    `No more challenges for ${owner} today. Try again in ${wait} 🌙`,
  // The server's own words for a shielded Keeper (territory service), so a tap
  // here reads the same as a refused challenge would.
  rivalShielded: 'This Keeper is new here. Their land is safe for now. Try wild land!',
  tooFar: 'Too far away! Try land next to yours.',
  watchCount: (n: number, max: number) => `On watch: ${String(n)} of ${String(max)}`,
  watchNone: 'Nobody stands watch here yet.',
  // Guards need a lit fire's light too (owner decision 2026-10-07).
  watchDark: "It's dark here at night. Build a fire nearby to keep your guard safe! 🔥",
  pick: 'Pick guards',
  save: 'Save',
  cancel: 'Never mind',
  pickTitle: (max: number) => `Who stands watch? Pick up to ${String(max)}.`,
  noSquishies: 'No squishy friends yet. Befriend one first!',
  elsewhere: 'on watch elsewhere',
  saved: 'Your guards are in place!',
  shielded: "You're new here, so nobody can challenge your land yet.",
  mystery: 'Mystery squishy',
  // Fences (#203, the owner-approved mockup).
  fenced: '🪵 Fenced! Break the fence, then beat the guard.',
  breakFirst: (fence: string, percent: number, cracks: string) =>
    `1 · Your first squishy breaks the ${fence} (${String(percent)}%). ${cracks}!`,
  thenGuard: '2 · Then the rest of your team beats the guard.',
  whoGoesFirst: 'Pick who goes first in Team.',
  keepGoingNote: 'Crack! The fence fell down! 🪵 Now beat the guard to make it yours.',
  keepGoing: 'Keep going!',
  keepGoingFree: 'Keep going is part of this challenge. No extra try.',
} as const;

export function createTerritoryScreen(options: TerritoryScreenOptions): TerritoryScreen {
  const api = options.api ?? territoryApi;
  const clock = new GameClock(options.now);

  let user: PublicUser | null = null;
  let mapId: string | null = null;
  let status: TerritoryStatus | null = null;
  /** Bumped by every map or user change, so a late reply can't land on another map. */
  let generation = 0;
  let working = false;
  let panel: { container: HTMLElement; tile: PublicTile; view: MapView } | null = null;
  let shown: TerritoryAction | null = null;
  /** The defender picker's choice while it's open. */
  let picking: string[] | null = null;
  let note = '';
  let ticker: number | undefined;
  /** The countdown on screen: a tile's rest, or when tries refill (#201). */
  let countdown: {
    node: HTMLElement;
    until: string;
    text: (wait: string) => string;
    /** Run out: the tile can be claimed again, or tries have refilled. */
    done: () => void;
  } | null = null;
  /** The countdown we already asked the server about, so it asks once. */
  let asked: string | null = null;

  const speciesName = (squishy: OwnedSquishy): string => {
    const species: Species | undefined =
      GAME_DATA.species.find((s) => s.id === squishy.speciesId) ??
      status?.speciesDefs.find((s) => s.id === squishy.speciesId);
    return squishy.nickname ?? species?.name ?? TERRITORY_TEXT.mystery;
  };

  async function refresh(): Promise<void> {
    const id = mapId;
    const at = generation;
    if (!id) return;
    try {
      const fresh = await api.status(id);
      if (at !== generation) return;
      clock.sync(fresh.now);
      status = fresh;
    } catch (err) {
      if (at === generation) note = messageOf(err);
    }
    if (at === generation) render();
  }

  const sendDeps = {
    newKey: newIdempotencyKey,
    wait: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
    retryAfterMs: COMMAND_RETRY_MS,
  };

  /** One command at a time; a `CONFLICT` (a rule said no, something changed) refetches. */
  async function act(run: (id: string, stillHere: () => boolean) => Promise<void>): Promise<void> {
    const id = mapId;
    if (!id || working) return;
    const at = generation;
    const stillHere = () => at === generation && mapId === id;
    working = true;
    note = '';
    render();
    try {
      await run(id, stillHere);
    } catch (err) {
      if (stillHere()) {
        note = messageOf(err);
        if (err instanceof ApiRequestError && err.code === 'CONFLICT') await refresh();
      }
    } finally {
      working = false;
      if (stillHere()) {
        render();
        revealNote();
      }
    }
  }

  const battleFor = (tile: PublicTile) =>
    act(async (id, stillHere) => {
      const battle = await sendCommand(
        sendDeps,
        (key) => api.attack(id, { q: tile.q, r: tile.r }, key),
        stillHere,
      );
      if (battle && stillHere()) options.openBattle(battle);
    });

  const saveGuards = (tile: PublicTile, squishyIds: string[]) =>
    act(async (id, stillHere) => {
      const fresh = await sendCommand(
        sendDeps,
        (key) => api.setDefenders(id, { q: tile.q, r: tile.r, squishyIds }, key),
        stillHere,
      );
      if (!fresh || !stillHere()) return;
      clock.sync(fresh.now);
      status = fresh;
      picking = null;
      note = TERRITORY_TEXT.saved;
    });

  // ── Drawing ───────────────────────────────────────────────────────────

  const line = (text: string, testId?: string) =>
    el('p', { class: 'tile-action-note', ...(testId ? { 'data-testid': testId } : {}) }, text);
  /** It's dark here at night, and the week of a Long Night, when he comes (#727). */
  const darkLine = () => {
    const p = line(TERRITORY_TEXT.watchDark, 'territory-dark');
    const soon = options.darkSoon?.() ?? null;
    if (soon) p.append(' ', el('span', { class: 'territory-dark-soon' }, soon));
    return p;
  };

  /** A line counting down to `until`; at zero it calls `done` once. */
  const countdownLine = (
    until: string,
    text: (wait: string) => string,
    done: () => void,
    testId?: string,
  ) => {
    const node = line(text(countdownAt(clock, until, asked).wait), testId);
    countdown = { node, until, text, done };
    return node;
  };
  /** Tries refill at the patch's midnight (`triesResetAt`): ask the server for the fresh count. */
  const triesLine = (resetAt: string, text: (wait: string) => string) =>
    countdownLine(resetAt, text, () => void refresh(), 'territory-tries');

  const button = (
    label: string,
    onTap: () => void,
    extra: Record<string, string> = {},
    soft = false,
  ) => {
    const b = el(
      'button',
      {
        type: 'button',
        class: `${buttonClass({ variant: soft ? 'secondary' : 'primary' })} bag-action`,
        ...extra,
      },
      label,
    );
    b.disabled = working;
    b.addEventListener('click', onTap);
    return b;
  };

  /** The defender picker: the player's squishies as big toggles, up to the max. */
  function picker(tile: PublicTile, chosen: string[]): HTMLElement[] {
    const max = TERRITORY_RULES.maxDefenders;
    const squishies = (status?.squishies ?? []).filter((s) => s.state === 'active');
    if (squishies.length === 0) {
      return [
        line(TERRITORY_TEXT.noSquishies),
        button(TERRITORY_TEXT.cancel, cancelPick, {}, true),
      ];
    }
    const elsewhere = new Set(
      (status?.defenders ?? [])
        .filter((d) => d.q !== tile.q || d.r !== tile.r)
        .flatMap((d) => d.squishyIds),
    );
    const list = el(
      'ul',
      { class: 'territory-picker', 'data-testid': 'territory-picker' },
      ...squishies.map((s) => {
        const on = chosen.includes(s.id);
        const toggle = el(
          'button',
          {
            type: 'button',
            class: 'territory-pick',
            'aria-pressed': on ? 'true' : 'false',
            'data-squishy': s.id,
          },
          el('span', { class: 'territory-pick-name' }, `${speciesName(s)} · Lv ${String(s.level)}`),
          ...(elsewhere.has(s.id)
            ? [el('span', { class: 'territory-pick-hint' }, TERRITORY_TEXT.elsewhere)]
            : []),
        );
        toggle.disabled = working || (!on && chosen.length >= max);
        toggle.addEventListener('click', () => {
          picking = on ? chosen.filter((id) => id !== s.id) : [...chosen, s.id];
          render();
        });
        return el('li', {}, toggle);
      }),
    );
    return [
      line(TERRITORY_TEXT.pickTitle(max)),
      list,
      el(
        'div',
        { class: 'territory-row' },
        button(TERRITORY_TEXT.save, () => void saveGuards(tile, chosen), {
          'data-testid': 'territory-save',
        }),
        button(TERRITORY_TEXT.cancel, cancelPick, {}, true),
      ),
    ];
  }

  function cancelPick(): void {
    picking = null;
    render();
  }

  /**
   * Who stands watch on a rival's tile (#531): how they feel, in the same
   * line as their count; "N squishies stand watch here" when the server sent
   * no feelings.
   */
  function guardsWho(tile: PublicTile): HTMLElement[] {
    // Only when it tells the same count (a live change or a guard taken to the
    // Hollow can leave the list behind): never a line about the wrong guards.
    const told = tile.defenderFeelings?.length === tile.defenders;
    const feelings = told ? watchFeelingsLine(tile.defenderFeelings) : null;
    if (feelings !== null) return [line(feelings, 'territory-guards-who')];
    return tile.defenders > 0 ? [line(TERRITORY_TEXT.onWatchThere(tile.defenders))] : [];
  }

  function render(): void {
    shown = null;
    countdown = null;
    if (!panel) {
      syncTicker();
      return;
    }
    const { container, tile, view } = panel;
    const action = territoryAction(
      tile,
      view,
      user?.id ?? null,
      status,
      clock.now(),
      options.supply?.(view),
    );
    shown = action;
    const children: HTMLElement[] = [];
    const owner = view.members.find((m) => m.user.id === tile.ownerUserId)?.user.username;
    switch (action.kind) {
      case 'none':
        if (tile.ownerUserId !== user?.id) children.push(...guardsWho(tile));
        break;
      case 'claim':
        children.push(
          line(TERRITORY_TEXT.claimNote),
          triesLine(action.triesResetAt, (wait) =>
            TERRITORY_TEXT.triesLeft(action.attemptsLeft, wait),
          ),
          button(TERRITORY_TEXT.claim, () => void battleFor(tile), { 'data-testid': 'tile-claim' }),
        );
        break;
      case 'challenge': {
        const card = action.fence ? fenceCard(action.fence) : null;
        children.push(
          ...(card
            ? [
                line(TERRITORY_TEXT.fenced, 'territory-fenced'),
                line(
                  TERRITORY_TEXT.breakFirst(card.name, card.percent, crackedBy(card.element)),
                  'territory-fence-plan',
                ),
                line(TERRITORY_TEXT.thenGuard),
                line(TERRITORY_TEXT.whoGoesFirst),
              ]
            : [line(TERRITORY_TEXT.challengeNote(owner ?? TERRITORY_TEXT.someone))]),
          ...guardsWho(tile),
          ...(showsGuardLevelNote(tile)
            ? [line(TERRITORY_TEXT.guardsAsStrong, 'territory-guards-as-strong')]
            : []),
          triesLine(action.triesResetAt, (wait) =>
            TERRITORY_TEXT.triesLeft(action.attemptsLeft, wait),
          ),
          button(TERRITORY_TEXT.challenge, () => void battleFor(tile), {
            'data-testid': 'tile-challenge',
          }),
        );
        break;
      }
      case 'keep-going':
        children.push(
          line(TERRITORY_TEXT.keepGoingNote, 'territory-keep-going-note'),
          button(TERRITORY_TEXT.keepGoing, () => void battleFor(tile), {
            'data-testid': 'territory-keep-going',
          }),
          line(TERRITORY_TEXT.keepGoingFree),
        );
        break;
      case 'resting':
        children.push(
          countdownLine(action.until, TERRITORY_TEXT.resting, render, 'territory-rest'),
        );
        break;
      case 'no-tries':
        children.push(triesLine(action.triesResetAt, TERRITORY_TEXT.noTries));
        break;
      case 'keeper-resting': {
        const reason = countdownLine(
          action.triesResetAt,
          (wait) => TERRITORY_TEXT.keeperResting(owner ?? TERRITORY_TEXT.someone, wait),
          () => void refresh(),
          'territory-keeper-resting',
        );
        reason.classList.add('territory-reason');
        children.push(reason);
        break;
      }
      case 'pvp-off':
        children.push(line(TERRITORY_TEXT.pvpOff));
        break;
      case 'shielded':
        children.push(...guardsWho(tile), line(TERRITORY_TEXT.rivalShielded, 'territory-shielded'));
        break;
      case 'too-far':
        children.push(
          ...(tile.ownerUserId !== null ? guardsWho(tile) : []),
          // A grey village's own block says how to reach it (#306); a
          // restored tile's block says Claim, so this says why it waits (PR 4).
          ...(tile.communityId && !isRestoredVillageTile(tile, view)
            ? []
            : [line(TERRITORY_TEXT.tooFar, 'territory-too-far')]),
        );
        break;
      case 'watch':
        if (picking) {
          if (watchInTheDark(tile, view)) {
            children.push(darkLine());
          }
          children.push(...picker(tile, picking));
        } else {
          const ids = [...action.squishyIds];
          if (status?.shieldUntil && clock.msUntil(status.shieldUntil) > 0) {
            children.push(line(TERRITORY_TEXT.shielded));
          }
          children.push(
            line(
              ids.length > 0
                ? TERRITORY_TEXT.watchCount(ids.length, TERRITORY_RULES.maxDefenders)
                : TERRITORY_TEXT.watchNone,
              'territory-watch',
            ),
            ...(watchInTheDark(tile, view) ? [darkLine()] : []),
            button(
              TERRITORY_TEXT.pick,
              () => {
                picking = ids;
                note = '';
                render();
              },
              { 'data-testid': 'territory-pick' },
              true,
            ),
          );
        }
        break;
    }
    // A refused Challenge that turned out to be the loss cap already says so.
    if (note && action.kind !== 'none' && action.kind !== 'keeper-resting') {
      // Right above the button that was tapped, so a refusal shows where the
      // player is looking, never under it below the panel's fold (#526).
      const at = children.findIndex((c) => c instanceof HTMLButtonElement);
      const noteLine = line(note, 'territory-note');
      noteLine.classList.add('territory-note');
      noteLine.setAttribute('role', 'status');
      children.splice(at === -1 ? children.length : at, 0, noteLine);
    }
    container.replaceChildren(...children);
    syncTicker();
  }

  /** Scrolls the tile panel to what a refused tap says, if anything (#526). */
  function revealNote(block: ScrollLogicalPosition = 'center'): void {
    panel?.container
      .querySelector<HTMLElement>(
        '[data-testid="territory-note"], [data-testid="territory-keeper-resting"]',
      )
      ?.scrollIntoView({ block });
  }

  /** Ticks once a second only while a countdown is on screen. */
  function syncTicker(): void {
    if (countdown && ticker === undefined) {
      ticker = window.setInterval(() => {
        if (!countdown) return;
        const { wait, ask } = countdownAt(clock, countdown.until, asked);
        const text = countdown.text(wait);
        if (countdown.node.textContent !== text) countdown.node.textContent = text;
        if (ask) {
          asked = countdown.until;
          countdown.done();
        }
      }, 1000);
    } else if (!countdown && ticker !== undefined) {
      window.clearInterval(ticker);
      ticker = undefined;
    }
  }

  const reset = () => {
    generation += 1;
    status = null;
    picking = null;
    note = '';
  };

  return {
    setMap: async (next) => {
      // The same map again (back from a battle): fetch fresh tries and guards.
      if (next !== mapId) reset();
      mapId = next;
      render();
      if (next) await refresh();
    },
    setUser: (next) => {
      if (next?.id === user?.id) return;
      user = next;
      mapId = null;
      reset();
      render();
    },
    tileActions: {
      show: (container, tile, view) => {
        const same = panel?.tile.q === tile.q && panel.tile.r === tile.r;
        if (!same) {
          picking = null;
          note = '';
        }
        panel = { container, tile, view };
        render();
        // A capped Keeper's land says why up front (#526). A small phone's
        // panel scrolls (map.css), and a wild squishy or a building above can
        // push the reason past its edge: scroll just far enough to show it,
        // and again once the panel has laid out. Only for a newly tapped
        // tile, so a redraw never fights the player's own scrolling.
        if (!same && shown?.kind === 'keeper-resting') {
          revealNote('nearest');
          requestAnimationFrame(() => {
            revealNote('nearest');
          });
        }
      },
      hide: () => {
        panel?.container.replaceChildren();
        panel = null;
        picking = null;
        note = '';
        render();
      },
    },
    get debug() {
      if (!mapId || !status) return null;
      return {
        mapId,
        attemptsLeft: status.attemptsLeft,
        onWatch: status.defenders.reduce((n, d) => n + d.squishyIds.length, 0),
        tileAction: shown?.kind ?? null,
        picking: picking !== null,
      };
    },
  };
}
